import { useCallback, useMemo } from 'react';
import { Button, Select } from 'antd';
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  Worksheet,
  TemplateSection,
  SectionType,
  ContextGroup,
  MeasurementGroup,
  FindingGroup,
  FlagGroup,
  FollowUpGroup,
} from '@/models/template';
import { useGetWorksheetSectionTypesQuery } from '@/state/slices/options';
import { EditableText } from './primitives/EditableText';
import { intToRoman } from './primitives/format';
import styles from './WorksheetSidebar.module.css';

const DEFAULT_ORDER: SectionType[] = [
  'CONTEXT',
  'MEASUREMENTS',
  'FINDINGS',
  'FOLLOW_UP',
  'CONCLUSION',
];

const SECTION_LABELS: Record<SectionType, string> = {
  CONTEXT: 'Context',
  MEASUREMENTS: 'Measurements',
  FINDINGS: 'Findings',
  FOLLOW_UP: 'Follow Up',
  CONCLUSION: 'Conclusion',
  FLAGS: 'Flags',
  SIGNATURES: 'Signatures',
  TAGS: 'Tags',
};

/* ────────────────────────────────────────────────────────────────────── */
/*  Drag id encoding                                                      */
/*    section:<sectionType>                                               */
/*    group:context:<uuid>                                                */
/*    group:measurement:<uuid>                                            */
/*    group:finding:<uuid>                                                */
/*    group:flag:<uuid>                                                   */
/*    group:followUp:<uuid>                                               */
/* ────────────────────────────────────────────────────────────────────── */

type GroupKind = 'context' | 'measurement' | 'finding' | 'flag' | 'followUp';

const sectionDragId = (type: SectionType) => `section:${type}`;
const groupDragId = (kind: GroupKind, uuid: string) => `group:${kind}:${uuid}`;
const parseDragId = (id: string) => id.split(':');

interface WorksheetSidebarProps {
  worksheet: Worksheet;
  onSectionsChange: (sections: TemplateSection[]) => void;
  onContextGroupsChange: (groups: ContextGroup[]) => void;
  onMeasurementGroupsChange: (groups: MeasurementGroup[]) => void;
  onFindingGroupsChange: (groups: FindingGroup[]) => void;
  onFlagGroupsChange: (groups: FlagGroup[]) => void;
  onFollowUpGroupsChange: (groups: FollowUpGroup[]) => void;
}

export function WorksheetSidebar({
  worksheet,
  onSectionsChange,
  onContextGroupsChange,
  onMeasurementGroupsChange,
  onFindingGroupsChange,
  onFlagGroupsChange,
  onFollowUpGroupsChange,
}: WorksheetSidebarProps) {
  const { data: sectionTypeOptions = [] } = useGetWorksheetSectionTypesQuery();

  const sectionOrder = useMemo<SectionType[]>(() => {
    if (!worksheet.sections || worksheet.sections.length === 0) {
      return DEFAULT_ORDER;
    }
    return [...worksheet.sections]
      .sort((a, b) => a.index - b.index)
      .map((s) => s.sectionType);
  }, [worksheet.sections]);

  const ensureMaterializedSections = useCallback((): TemplateSection[] => {
    if (worksheet.sections && worksheet.sections.length > 0) {
      return [...worksheet.sections].sort((a, b) => a.index - b.index);
    }
    return DEFAULT_ORDER.map<TemplateSection>((type, index) => ({
      uuid: crypto.randomUUID(),
      sectionType: type,
      index,
      groups: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
  }, [worksheet.sections]);

  const handleAddSection = useCallback(
    (sectionType: SectionType) => {
      const existing = ensureMaterializedSections();
      if (existing.some((s) => s.sectionType === sectionType)) return;
      const now = new Date().toISOString();
      const next: TemplateSection = {
        uuid: crypto.randomUUID(),
        sectionType,
        index: existing.length,
        groups: [],
        createdAt: now,
        updatedAt: now,
      };
      onSectionsChange([...existing, next]);
    },
    [ensureMaterializedSections, onSectionsChange]
  );

  const handleDeleteSection = useCallback(
    (sectionType: SectionType) => {
      const existing = ensureMaterializedSections();
      const filtered = existing
        .filter((s) => s.sectionType !== sectionType)
        .map((s, i) => ({ ...s, index: i }));
      onSectionsChange(filtered);
    },
    [ensureMaterializedSections, onSectionsChange]
  );

  const availableSectionTypes = useMemo(
    () =>
      sectionTypeOptions.filter(
        (t) => !sectionOrder.includes(t as SectionType)
      ),
    [sectionTypeOptions, sectionOrder]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const reorderSections = useCallback(
    (oldType: SectionType, newType: SectionType) => {
      const existing =
        worksheet.sections && worksheet.sections.length > 0
          ? [...worksheet.sections].sort((a, b) => a.index - b.index)
          : DEFAULT_ORDER.map<TemplateSection>((type, index) => ({
              uuid: crypto.randomUUID(),
              sectionType: type,
              index,
              groups: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }));
      const oldIdx = existing.findIndex((s) => s.sectionType === oldType);
      const newIdx = existing.findIndex((s) => s.sectionType === newType);
      if (oldIdx === -1 || newIdx === -1) return;
      const moved = arrayMove(existing, oldIdx, newIdx);
      onSectionsChange(moved.map((s, i) => ({ ...s, index: i })));
    },
    [worksheet.sections, onSectionsChange]
  );

  const reorderGroupArray = useCallback(
    <G extends { uuid: string }>(
      arr: G[],
      activeUuid: string,
      overUuid: string
    ): G[] | null => {
      const oldIdx = arr.findIndex((g) => g.uuid === activeUuid);
      const newIdx = arr.findIndex((g) => g.uuid === overUuid);
      if (oldIdx === -1 || newIdx === -1) return null;
      return arrayMove(arr, oldIdx, newIdx);
    },
    []
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeParts = parseDragId(String(active.id));
      const overParts = parseDragId(String(over.id));

      // Dragging a section: resolve the "over" target to a section even if
      // dnd-kit's collision detection landed on a group inside one.
      if (activeParts[0] === 'section') {
        let overSection: SectionType | undefined;
        if (overParts[0] === 'section') {
          overSection = overParts[1] as SectionType;
        } else if (overParts[0] === 'group') {
          const kind = overParts[1] as GroupKind;
          if (kind === 'context') overSection = 'CONTEXT';
          else if (kind === 'measurement') overSection = 'MEASUREMENTS';
          else if (kind === 'finding' || kind === 'flag')
            overSection = 'FINDINGS';
          else if (kind === 'followUp') overSection = 'FOLLOW_UP';
        }
        if (overSection) {
          reorderSections(activeParts[1] as SectionType, overSection);
        }
        return;
      }

      if (activeParts[0] === 'group' && activeParts[1] === overParts[1]) {
        const kind = activeParts[1] as GroupKind;
        const activeUuid = activeParts[2];
        const overUuid = overParts[2];

        if (kind === 'context') {
          const next = reorderGroupArray(
            worksheet.contextGroups,
            activeUuid,
            overUuid
          );
          if (next) onContextGroupsChange(next);
        } else if (kind === 'measurement') {
          const sorted = [...worksheet.measurementGroups].sort(
            (a, b) => a.index - b.index
          );
          const next = reorderGroupArray(sorted, activeUuid, overUuid);
          if (next) {
            onMeasurementGroupsChange(next.map((g, i) => ({ ...g, index: i })));
          }
        } else if (kind === 'finding') {
          const sorted = [...worksheet.findingGroups].sort(
            (a, b) => (a.index ?? 0) - (b.index ?? 0)
          );
          const next = reorderGroupArray(sorted, activeUuid, overUuid);
          if (next) {
            onFindingGroupsChange(next.map((g, i) => ({ ...g, index: i })));
          }
        } else if (kind === 'flag') {
          const sorted = [...worksheet.flagGroups].sort(
            (a, b) => (a.index ?? 0) - (b.index ?? 0)
          );
          const next = reorderGroupArray(sorted, activeUuid, overUuid);
          if (next) {
            onFlagGroupsChange(next.map((g, i) => ({ ...g, index: i })));
          }
        } else if (kind === 'followUp') {
          const next = reorderGroupArray(
            worksheet.followUpGroups ?? [],
            activeUuid,
            overUuid
          );
          if (next) onFollowUpGroupsChange(next);
        }
      }
    },
    [
      reorderSections,
      reorderGroupArray,
      worksheet.contextGroups,
      worksheet.measurementGroups,
      worksheet.findingGroups,
      worksheet.flagGroups,
      worksheet.followUpGroups,
      onContextGroupsChange,
      onMeasurementGroupsChange,
      onFindingGroupsChange,
      onFlagGroupsChange,
      onFollowUpGroupsChange,
    ]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sectionOrder.map(sectionDragId)}
        strategy={verticalListSortingStrategy}
      >
        <div className={styles.sidebar}>
          {sectionOrder.map((type, idx) => (
            <SortableSection
              key={type}
              sectionType={type}
              position={idx + 1}
              worksheet={worksheet}
              onDelete={() => handleDeleteSection(type)}
              onContextGroupsChange={onContextGroupsChange}
              onMeasurementGroupsChange={onMeasurementGroupsChange}
              onFindingGroupsChange={onFindingGroupsChange}
              onFlagGroupsChange={onFlagGroupsChange}
              onFollowUpGroupsChange={onFollowUpGroupsChange}
            />
          ))}
          {availableSectionTypes.length > 0 && (
            <div className={styles.addSectionWrap}>
              <Select
                size="small"
                placeholder="+ Add section"
                value={undefined}
                options={availableSectionTypes.map((t) => ({
                  value: t,
                  label: SECTION_LABELS[t as SectionType] ?? t,
                }))}
                onChange={(v) => v && handleAddSection(v as SectionType)}
                style={{ width: '100%' }}
              />
            </div>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Sortable section block                                                */
/* ────────────────────────────────────────────────────────────────────── */

interface SortableSectionProps {
  sectionType: SectionType;
  position: number;
  worksheet: Worksheet;
  onDelete: () => void;
  onContextGroupsChange: (groups: ContextGroup[]) => void;
  onMeasurementGroupsChange: (groups: MeasurementGroup[]) => void;
  onFindingGroupsChange: (groups: FindingGroup[]) => void;
  onFlagGroupsChange: (groups: FlagGroup[]) => void;
  onFollowUpGroupsChange: (groups: FollowUpGroup[]) => void;
}

function SortableSection({
  sectionType,
  position,
  worksheet,
  onDelete,
  onContextGroupsChange,
  onMeasurementGroupsChange,
  onFindingGroupsChange,
  onFlagGroupsChange,
  onFollowUpGroupsChange,
}: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sectionDragId(sectionType) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.sectionBlock}>
      <div
        className={styles.sectionHeader}
        data-editable-section
        {...attributes}
        {...listeners}
        aria-label={`Drag ${SECTION_LABELS[sectionType]} section`}
      >
        <span className={styles.dragHandle} aria-hidden="true">
          <HolderOutlined />
        </span>
        <span className={styles.sectionTitle}>
          {intToRoman(position)}. {SECTION_LABELS[sectionType]}
        </span>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onDelete}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={`Delete ${SECTION_LABELS[sectionType]} section`}
        >
          <DeleteOutlined />
        </button>
      </div>

      {sectionType === 'CONTEXT' && (
        <SortableGroupList
          kind="context"
          groups={worksheet.contextGroups}
          onChange={onContextGroupsChange}
          factory={() => ({
            uuid: crypto.randomUUID(),
            label: 'New Group',
            contextLabels: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })}
        />
      )}

      {sectionType === 'MEASUREMENTS' && (
        <SortableGroupList
          kind="measurement"
          groups={[...worksheet.measurementGroups].sort(
            (a, b) => a.index - b.index
          )}
          onChange={onMeasurementGroupsChange}
          factory={(maxIndex) => ({
            uuid: crypto.randomUUID(),
            label: 'New Group',
            index: maxIndex + 1,
            measurementLabels: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })}
        />
      )}

      {sectionType === 'FINDINGS' && (
        <>
          <div className={styles.subTitle}>Finding Groups</div>
          <SortableGroupList
            kind="finding"
            groups={[...worksheet.findingGroups].sort(
              (a, b) => (a.index ?? 0) - (b.index ?? 0)
            )}
            onChange={onFindingGroupsChange}
            factory={(maxIndex) => ({
              uuid: crypto.randomUUID(),
              label: 'New Group',
              index: maxIndex + 1,
              style: null,
              findingLabels: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })}
          />
          <div className={styles.subTitle}>Flag Groups</div>
          <SortableGroupList
            kind="flag"
            groups={[...worksheet.flagGroups].sort(
              (a, b) => (a.index ?? 0) - (b.index ?? 0)
            )}
            onChange={onFlagGroupsChange}
            factory={(maxIndex) => ({
              uuid: crypto.randomUUID(),
              label: 'New Group',
              index: maxIndex + 1,
              style: '',
              flagLabels: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })}
          />
        </>
      )}

      {sectionType === 'FOLLOW_UP' && (
        <SortableGroupList
          kind="followUp"
          groups={worksheet.followUpGroups ?? []}
          onChange={onFollowUpGroupsChange}
          factory={() => ({
            uuid: crypto.randomUUID(),
            label: 'New Group',
            style: '',
            followUpLabels: [],
          })}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Sortable group list                                                   */
/* ────────────────────────────────────────────────────────────────────── */

interface GroupShape {
  uuid: string;
  label: string;
}

interface SortableGroupListProps<G extends GroupShape> {
  kind: GroupKind;
  groups: G[];
  onChange: (groups: G[]) => void;
  factory: (maxIndex: number) => G;
}

function SortableGroupList<G extends GroupShape>({
  kind,
  groups,
  onChange,
  factory,
}: SortableGroupListProps<G>) {
  const handleLabelChange = useCallback(
    (idx: number, label: string) => {
      const next = [...groups];
      next[idx] = { ...next[idx], label };
      onChange(next);
    },
    [groups, onChange]
  );

  const handleDelete = useCallback(
    (idx: number) => {
      onChange(groups.filter((_, i) => i !== idx));
    },
    [groups, onChange]
  );

  const handleAdd = useCallback(() => {
    const maxIndex = groups.reduce(
      (m, g) => Math.max(m, (g as { index?: number }).index ?? 0),
      -1
    );
    onChange([...groups, factory(maxIndex)]);
  }, [groups, factory, onChange]);

  return (
    <>
      <SortableContext
        items={groups.map((g) => groupDragId(kind, g.uuid))}
        strategy={verticalListSortingStrategy}
      >
        <ul className={styles.groupList}>
          {groups.map((g, idx) => (
            <SortableGroupItem
              key={g.uuid}
              kind={kind}
              group={g}
              position={idx + 1}
              onLabelChange={(label) => handleLabelChange(idx, label)}
              onDelete={() => handleDelete(idx)}
            />
          ))}
        </ul>
      </SortableContext>
      <div className={styles.addGroupRow}>
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          block
        >
          Add group
        </Button>
      </div>
    </>
  );
}

interface SortableGroupItemProps<G extends GroupShape> {
  kind: GroupKind;
  group: G;
  position: number;
  onLabelChange: (label: string) => void;
  onDelete: () => void;
}

function SortableGroupItem<G extends GroupShape>({
  kind,
  group,
  position,
  onLabelChange,
  onDelete,
}: SortableGroupItemProps<G>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: groupDragId(kind, group.uuid) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={styles.groupItem}
      data-editable-group
      {...attributes}
      {...listeners}
      aria-label="Drag to reorder"
    >
      <span className={styles.dragHandle} aria-hidden="true">
        <HolderOutlined />
      </span>
      <span className={styles.positionBadge}>{position}.</span>
      <span className={styles.groupLabel}>
        <EditableText
          value={group.label}
          onChange={onLabelChange}
          placeholder="Group name"
        />
      </span>
      <button
        type="button"
        className={styles.iconButton}
        onClick={onDelete}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="Delete group"
      >
        <DeleteOutlined />
      </button>
    </li>
  );
}
